import { findLibrariesFolder, findLibraryFolder, removeLibrary } from '@/adapter/libraryProjection';
import ConfirmPopover from '@/components/common/ConfirmPopover';
import FadeSlide from '@/components/common/FadeSlide';
import AddLibraryDialog from '@/components/navigation/wizard/library/AddLibraryDialog';
import { SubroutineTreeItemsWrapperProps } from '@/components/treemenu/SubroutineTreeItems';
import { SelectedItem } from '@/components/treemenu/TreeMenu';
import { useApplication } from '@/hooks/useApplication';
import { useDialogs } from '@/hooks/useDialogs';
import { useMetadata } from '@/hooks/useMetadata';
import { useSubroutine } from '@/hooks/useSubroutine';
import { useTreeContextMenu } from '@/hooks/useTreeContextMenu';
import { useUpdateNodes } from '@/hooks/useUpdateNodes';
import { SubroutineSortType } from '@/types/subroutine';
import { getClipboardData, handlePasteSubroutinesIntoApplication } from '@/util/clipboard';
import { deleteSubroutine } from '@/util/delete';
import { dismissOnContextMenu } from '@/util/menu';
import { copyItem } from '@/util/duplicate';
import { isClipboardSubroutineData } from '@/util/typeGuards';
import { Clear, Delete } from '@mui/icons-material';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import Box from '@mui/material/Box';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu, { MenuProps } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { FC, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import AuthorizedMenuItem from './menu-items/AuthorizedMenuItem';
import MoveToFolderMenuItem from './menu-items/MoveToFolderMenuItem';
import SubroutineCopyMenuItem from './menu-items/SubroutineCopyMenuItem';
import SubroutineEditMenuItem from './menu-items/SubroutineEditMenuItem';
import SubroutineNewMenuItem from './menu-items/SubroutineNewMenuItem';
import SubroutinePasteMenuItem from './menu-items/SubroutinePasteMenuItem';
import SubroutineFolder from './SubroutineFolder';
import SubroutineSort from './SubroutineSort';

const SubroutineContextMenuWrapper: FC<SubroutineTreeItemsWrapperProps> = (props) => {
  const auth = useAuth();
  const { application, setApplication } = useApplication();
  const { setUpdateNodes } = useUpdateNodes();
  const { metadata } = useMetadata();
  const { selectedSubroutineId, setSelectedSubroutineId } = useSubroutine();

  const { contextMenu, handleContextMenu, handleClose } = useTreeContextMenu();
  const { confirm } = useDialogs();
  const [copiedIds, setCopiedIds] = useState<number>(0);
  const [showPasteOption, setShowPasteOption] = useState<'subroutine' | 'folder'>();
  const [openAddLibraryDialog, setOpenAddLibraryDialog] = useState(false);

  const selectedItem: SelectedItem = {
    id: props.subroutine?.id,
    folderId: props.subroutine?.id
      ? props.subroutine?.folder
      : application.folders?.find((folder) => folder.id === props.folderId)?.parentId,
    name: props.subroutine?.label || application.folders?.find((folder) => folder.id === props.folderId)?.label,
    type: props.subroutine?.id ? 'subroutine' : 'folder'
  };

  const handleSort = (sortBy: SubroutineSortType) => {
    props.handleOnSort && props.handleOnSort(sortBy);
    handleClose();
  };

  const handleDelete = () => {
    if (!props.subroutine?.id) return;
    console.debug('DELETE', props.subroutine.id);
    if (selectedSubroutineId === props.subroutine.id) {
      setSelectedSubroutineId(application.subroutines[0].id);
    }
    const updatedApplication = deleteSubroutine({ ...application }, props.subroutine.id);
    setApplication(updatedApplication);
    setUpdateNodes(['*']);
    handleClose();
  };

  // Subroutine rows carry their containing folder's id, so this covers folders and links alike.
  const libraryFolder = findLibraryFolder(application, props.folderId);
  const librariesFolder = findLibrariesFolder(application);
  const isLibrariesRoot = Boolean(librariesFolder && !props.subroutine && librariesFolder.id === props.folderId);

  const handleDeleteLibrary = () => {
    if (!libraryFolder) return;
    handleClose();

    confirm({
      title: `Removing library: ${libraryFolder.label}`,
      description: `Are you sure you want to remove the following library: ${libraryFolder.label}? Blocks calling its subroutines will report errors.`,
      confirmationText: 'Remove',
      cancellationButtonProps: { color: 'secondary' }
    }).then((result) => {
      if (!result.confirmed) return;

      const removed = removeLibrary(application, libraryFolder);
      if (!removed.subroutines.some((subroutine) => subroutine.id === selectedSubroutineId)) {
        setSelectedSubroutineId(removed.subroutines[0].id);
      }
      setApplication(removed);
      setUpdateNodes(['*']);
    });
  };

  const handlePaste = () => {
    getClipboardData()
      .then((clipboardData) => {
        console.debug('handlePaste.then(clipboardData)', clipboardData);
        if (!isClipboardSubroutineData(clipboardData)) return;

        const { newApplication, selectedId } = handlePasteSubroutinesIntoApplication(
          application,
          metadata,
          clipboardData,
          props.folderId
        );
        setApplication(newApplication);
        if (clipboardData.folders.length) {
          props.setSelectedTreeMenuItem && props.setSelectedTreeMenuItem(`folders.${selectedId}`);
        } else {
          setSelectedSubroutineId(selectedId);
        }
      })
      .finally(() => {
        setUpdateNodes(['*']);
        handleClose();
      });
  };

  useEffect(() => {
    if (contextMenu !== null) {
      getClipboardData()
        .then((clipboardData) => {
          if (isClipboardSubroutineData(clipboardData)) {
            setShowPasteOption(clipboardData.folders.length ? 'folder' : 'subroutine');
          } else {
            setShowPasteOption(undefined);
          }
        })
        .catch(() => setShowPasteOption(undefined));
    } else {
      setShowPasteOption(undefined);
    }
  }, [contextMenu]);

  const menuProps: MenuProps = {
    open: contextMenu !== null,
    onClose: handleClose,
    anchorReference: 'anchorPosition',
    anchorPosition: contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined,
    slotProps: dismissOnContextMenu(handleClose)
  };

  return (
    <Box onContextMenu={handleContextMenu} flexGrow={1}>
      {props.children}
      {isLibrariesRoot && (
        <Menu {...menuProps}>
          <AuthorizedMenuItem
            onClick={() => {
              auth.isAuthenticated && setOpenAddLibraryDialog(true);
              handleClose();
            }}
          >
            <ListItemIcon>
              <LibraryAddIcon fontSize='small' />
            </ListItemIcon>
            Add library
          </AuthorizedMenuItem>
        </Menu>
      )}
      {libraryFolder && !props.subroutine && (
        <Menu {...menuProps}>
          <MenuItem onClick={handleDeleteLibrary}>
            <ListItemIcon>
              <Delete fontSize='small' />
            </ListItemIcon>
            Remove library
          </MenuItem>
        </Menu>
      )}
      {!isLibrariesRoot && !libraryFolder && (
        <Menu {...menuProps}>
          {showPasteOption && <SubroutinePasteMenuItem contextType={showPasteOption} onSubmit={handlePaste} />}
          <SubroutineNewMenuItem
            openEdit={(openEdit) => {
              props.openEdit(openEdit);
              handleClose();
            }}
            onClose={handleClose}
            folderId={props.folderId}
          />
          {(props.subroutine?.id || props.folderId) && (
            <SubroutineEditMenuItem
              openEdit={(openEdit) => {
                props.openEdit(openEdit);
                handleClose();
              }}
              onClose={handleClose}
              subroutineId={props.subroutine?.id}
              folderId={props.folderId}
            />
          )}
          {props.subroutine?.id && (
            <ConfirmPopover
              component='MenuItem'
              onConfirm={handleDelete}
              icon={<Clear />}
              buttonText='Delete subroutine'
              text={`Remove ${props.subroutine?.label} and its related blocks?`}
            />
          )}
          {(props.subroutine?.id || props.folderId) && (
            <SubroutineCopyMenuItem
              type={props.subroutine?.id ? 'subroutine' : 'folder'}
              onSubmit={() => {
                copyItem(application, selectedItem, props.folderId).then((ids) => {
                  setCopiedIds(ids);
                  handleClose();
                });
              }}
            />
          )}
          {(props.subroutine?.id || props.folderId) && (
            <MoveToFolderMenuItem
              onConfirm={() => {
                props.openHandleFoldersDialog({
                  id: props.subroutine?.id || props.folderId,
                  folderId: props.subroutine?.id
                    ? props.subroutine?.folder
                    : application.folders?.find((folder) => folder.id === props.folderId)?.parentId,
                  name:
                    props.subroutine?.label ||
                    application.folders?.find((folder) => folder.id === props.folderId)?.label,
                  type: props.subroutine?.id ? 'subroutine' : 'folder'
                });
                handleClose();
              }}
            />
          )}

          <SubroutineFolder
            subroutineId={props.subroutine?.id}
            folderId={props.folderId}
            newFolder={() => {
              props.openHandleFoldersDialog({
                folderId: props.subroutine?.id
                  ? props.subroutine?.folder
                  : application.folders?.find((folder) => folder.id === props.folderId)?.parentId
              });
              handleClose();
            }}
            deleteFolder={(folderId) => {
              props.deleteFolder && props.deleteFolder(folderId);
              handleClose();
            }}
          />
          {props.isRoot && <SubroutineSort onSort={(sortBy) => handleSort(sortBy)} />}
        </Menu>
      )}
      {copiedIds > 0 && (
        <Stack
          position='absolute'
          top={0}
          width='100%'
          height='100%'
          justifyContent='center'
          alignItems='center'
          sx={{ pointerEvents: 'none' }}
        >
          <FadeSlide
            message={`Copied ${copiedIds} subroutine${copiedIds === 1 ? '' : 's'}!`}
            onDone={() => setCopiedIds(0)}
          />
        </Stack>
      )}
      {isLibrariesRoot && (
        <AddLibraryDialog open={openAddLibraryDialog} onClose={() => setOpenAddLibraryDialog(false)} />
      )}
    </Box>
  );
};

export default SubroutineContextMenuWrapper;
